import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { listFirmExpenses, createFirmExpense, approveFirmExpense, payFirmExpense, cancelRecurringExpense, listSuppliers, listGLAccounts } from '../api';
import { formatCurrency } from '../utils/currency';
import { useConfirm } from './ConfirmProvider';

const CATEGORY_OPTIONS = [
  { value: 'RENT', label: 'Rent' },
  { value: 'UTILITIES', label: 'Utilities' },
  { value: 'TELEPHONE', label: 'Telephone' },
  { value: 'INTERNET', label: 'Internet' },
  { value: 'INSURANCE', label: 'Insurance' },
  { value: 'SUBSCRIPTIONS', label: 'Subscriptions' },
  { value: 'POSTAGE', label: 'Postage & Courier' },
  { value: 'STATIONERY', label: 'Stationery' },
  { value: 'TRANSPORT', label: 'Transport' },
  { value: 'CLEANING', label: 'Cleaning' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'BANK_CHARGES', label: 'Bank Charges' },
  { value: 'REFRESHMENTS', label: 'Refreshments (Coffee, Water, etc.)' },
  { value: 'OTHER', label: 'Other' },
];

const STATUS_COLORS = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  APPROVED: 'bg-blue-100 text-blue-700',
  PAID: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-600',
  REVERSED: 'bg-gray-300 text-gray-700',
};

function FirmExpenseManagement({ authAxios, showFormMessage, userRole }) {
  const confirm = useConfirm();
  const [expenses, setExpenses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [payingId, setPayingId] = useState(null);
  const [payMethod, setPayMethod] = useState('EFT');
  const [formData, setFormData] = useState({
    description: '', category: 'OTHER', amount: '', date: new Date().toISOString().slice(0, 10),
    supplier: '', gl_account: '', reference: '', is_recurring: false, recurrence_interval: 'MONTHLY', notes: '',
  });

  const canManage = useMemo(() => ['PARTNER', 'SUPERUSER', 'ACCOUNTANT'].includes(userRole), [userRole]);
  const canApprove = useMemo(() => ['PARTNER', 'SUPERUSER'].includes(userRole), [userRole]);

  const fetchExpenses = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      const data = await listFirmExpenses(authAxios, params);
      setExpenses(Array.isArray(data) ? data : []);
    } catch { showFormMessage('error', 'Failed to load expenses.'); }
    finally { setIsLoading(false); }
  }, [authAxios, showFormMessage, statusFilter, categoryFilter]);

  const fetchRef = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([listSuppliers(authAxios, { is_active: true }), listGLAccounts(authAxios, { is_active: true, category: 'EXPENSE' })]);
      setSuppliers(Array.isArray(s) ? s : []);
      setAccounts(Array.isArray(a) ? a : []);
    } catch {}
  }, [authAxios]);

  useEffect(() => { fetchExpenses(); fetchRef(); }, [fetchExpenses, fetchRef]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (!payload.supplier) delete payload.supplier;
      if (!payload.gl_account) delete payload.gl_account;
      await createFirmExpense(authAxios, payload);
      showFormMessage('success', 'Expense submitted.');
      setShowForm(false);
      fetchExpenses();
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Failed to submit expense.'); }
  };

  const handleApprove = async (id) => {
    try {
      await approveFirmExpense(authAxios, id);
      showFormMessage('success', 'Expense approved.');
      fetchExpenses();
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Failed to approve.'); }
  };

  const handlePay = async (id) => {
    try {
      await payFirmExpense(authAxios, id, { payment_method: payMethod });
      showFormMessage('success', 'Expense paid.');
      setPayingId(null);
      fetchExpenses();
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Failed to record payment.'); }
  };

  const handleCancelRecurring = async (id) => {
    const ok = await confirm({
      title: 'Cancel Recurring Expense',
      message: 'Cancel future auto-generation for this recurring expense?',
      confirmText: 'Cancel Recurring',
      variant: 'warning',
    });
    if (!ok) return;
    try {
      await cancelRecurringExpense(authAxios, id);
      showFormMessage('success', 'Recurring schedule cancelled.');
      fetchExpenses();
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Failed to cancel recurring.'); }
  };

  const filtered = useMemo(() => {
    if (!searchTerm) return expenses;
    const term = searchTerm.toLowerCase();
    return expenses.filter(e => (e.description || '').toLowerCase().includes(term) || (e.reference || '').toLowerCase().includes(term));
  }, [expenses, searchTerm]);

  // Summary totals
  const totals = useMemo(() => {
    const pending = expenses.filter(e => e.status === 'PENDING').reduce((s, e) => s + parseFloat(e.amount || 0), 0);
    const approved = expenses.filter(e => e.status === 'APPROVED').reduce((s, e) => s + parseFloat(e.amount || 0), 0);
    const paid = expenses.filter(e => e.status === 'PAID').reduce((s, e) => s + parseFloat(e.amount || 0), 0);
    return { pending, approved, paid };
  }, [expenses]);

  const updateField = (field, value) => setFormData(f => ({ ...f, [field]: value }));

  return (
    <div className="animate__animated animate__fadeIn">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Firm Expenses</h2>
          <p className="text-gray-500 text-sm mt-1">Track rent, utilities, subscriptions, and general expenses</p>
        </div>
        {canManage && (
          <button onClick={() => setShowForm(true)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium">+ New Expense</button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 uppercase">Pending Approval</p>
          <p className="text-lg font-bold text-yellow-600">{formatCurrency(totals.pending)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 uppercase">Approved (Unpaid)</p>
          <p className="text-lg font-bold text-blue-600">{formatCurrency(totals.approved)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 uppercase">Paid This Period</p>
          <p className="text-lg font-bold text-green-600">{formatCurrency(totals.paid)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input type="text" placeholder="Search expenses..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">All Statuses</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="PAID">Paid</option>
          <option value="REJECTED">Rejected</option>
          <option value="REVERSED">Reversed</option>
        </select>
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">All Categories</option>
          {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* Expense List */}
      {isLoading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm"><p className="text-gray-500">No expenses found.</p></div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Recurring</th>
                {canManage && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(e => (
                <tr key={e.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-700">{e.date}</td>
                  <td className="px-4 py-3 text-gray-800">{e.description}</td>
                  <td className="px-4 py-3 text-gray-600">{CATEGORY_OPTIONS.find(c => c.value === e.category)?.label || e.category}</td>
                  <td className="px-4 py-3 text-gray-600">{e.supplier_name || '—'}</td>
                  <td className="px-4 py-3 text-right font-medium">{formatCurrency(e.amount || 0)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[e.status] || 'bg-gray-100 text-gray-600'}`}>
                      {e.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {e.is_recurring ? (
                      <span className="inline-flex items-center gap-1">
                        <span className="text-blue-600 font-medium text-xs">{e.recurrence_interval}</span>
                        {e.recurring_is_cancelled && <span className="text-red-500 text-[10px] font-bold">(Cancelled)</span>}
                      </span>
                    ) : e.is_auto_generated ? (
                      <span className="text-xs text-gray-400 italic">Auto</span>
                    ) : '—'}
                  </td>
                  {canManage && (
                    <td className="px-4 py-3 text-right space-x-2">
                      {e.status === 'PENDING' && canApprove && (
                        <button onClick={() => handleApprove(e.id)} className="text-green-600 hover:text-green-800 text-xs font-medium">Approve</button>
                      )}
                      {e.status === 'APPROVED' && (
                        <button onClick={() => { setPayingId(e.id); setPayMethod('EFT'); }} className="text-blue-600 hover:text-blue-800 text-xs font-medium">Pay</button>
                      )}
                      {e.is_recurring && !e.recurring_is_cancelled && (
                        <button onClick={() => handleCancelRecurring(e.id)} className="text-orange-600 hover:text-orange-800 text-xs font-medium">Cancel Recurring</button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New Expense Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">New Firm Expense</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                  <input type="text" required value={formData.description} onChange={e => updateField('description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. BPC electricity bill – January" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select value={formData.category} onChange={e => updateField('category', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (BWP) *</label>
                  <input type="number" step="0.01" required value={formData.amount} onChange={e => updateField('amount', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input type="date" required value={formData.date} onChange={e => updateField('date', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                  <select value={formData.supplier} onChange={e => updateField('supplier', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="">None</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GL Account</label>
                  <select value={formData.gl_account} onChange={e => updateField('gl_account', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="">Auto-assign by category</option>
                    {accounts.map(a => <option key={a.id} value={a.id}>{a.code} – {a.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
                <input type="text" value={formData.reference} onChange={e => updateField('reference', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Invoice/receipt number" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={formData.is_recurring} onChange={e => updateField('is_recurring', e.target.checked)} />
                Recurring Expense
              </label>
              {formData.is_recurring && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recurrence</label>
                  <select value={formData.recurrence_interval} onChange={e => updateField('recurrence_interval', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="WEEKLY">Weekly</option>
                    <option value="MONTHLY">Monthly</option>
                    <option value="QUARTERLY">Quarterly</option>
                    <option value="ANNUALLY">Annually</option>
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={formData.notes} onChange={e => updateField('notes', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={2} />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">Submit Expense</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Pay Modal */}
      {payingId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Record Payment</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select value={payMethod} onChange={e => setPayMethod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="EFT">EFT</option>
                  <option value="CHEQUE">Cheque</option>
                  <option value="CASH">Cash</option>
                  <option value="CARD">Card</option>
                  <option value="DEBIT_ORDER">Debit Order</option>
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setPayingId(null)} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
                <button onClick={() => handlePay(payingId)} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm">Mark Paid</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FirmExpenseManagement;
